import { Editor } from '@monaco-editor/react';
import { Box } from '@mui/material';
import { Document, PDFViewer } from '@react-pdf/renderer';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';

import { DOCUMENT_SIZES, renderPages, useDocumentData, useDocumentPreview } from 'src/modules/entities/documents';

export default function DocumentEditorPreview({
  open,
  css,
  html,
  entity,
  type,
  generationOptions,
  templateId,
  queryVariables,
}) {
  const documentGetter = useDocumentData({ entityType: entity, variables: queryVariables });

  const [documentData, setDocumentData] = useState<unknown>(null);

  useEffect(() => {
    if (!entity) {
      return;
    }

    documentGetter
      .getDocumentData()
      .then(setDocumentData)
      .catch((e) => {
        console.error(e);
      });
  }, [documentGetter, entity]);

  if (!documentData || !open) {
    return null;
  }

  return (
    <>
      <DocumentPreviewWithSource
        data={documentData}
        type={type}
        entityType={entity}
        {...DOCUMENT_SIZES.large}
        documentTemplate={
          open && documentData
            ? {
                id: templateId,
                generationOptions: typeof generationOptions === 'object' ? generationOptions : {},
                pages: [{ css, html }],
              }
            : null
        }
      />
    </>
  );
}

function DocumentPreviewWithSource({
  width,
  height,
  data,
  documentTemplate,
  entityType,
  type,
  loading,
  extendedData,
}: {
  width: string;
  height: string;
  data: unknown;
  documentTemplate?: DocumentType;
  type: string;
  loading: boolean;
  extendedData?: Record<string | number, unknown>;
}) {
  const pages = useDocumentPreview({ data, documentTemplate, entityType, type, extendedData });
  const [html, setHtml] = useState(pages?.[0]);
  const flags = useFlags();

  useEffect(() => {
    setHtml(pages?.[0] ?? '');
  }, [pages]);

  if (!pages) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', width: '2000px' }}>
      <PDFViewer width={width} height={height} showToolbar={false} style={{ border: 'none', borderRadius: '4px' }}>
        <Document>
          {renderPages([html], {
            ...(flags.documentSettings ?? {}),
            hideFooter: ['BALANCE_SHEET_REPORT', 'PROFIT_AND_LOSS_REPORT'].includes(entityType), // just hard coding this for now
          })}
        </Document>
      </PDFViewer>
      <Editor
        theme="vs-dark"
        defaultLanguage="html"
        value={html}
        onChange={(e) => {
          setHtml(e);
        }}
        width="1000px"
        height="1000px"
      />
    </Box>
  );
}
